import { useMutation, useQuery } from "@tanstack/react-query";

import { api } from "./ApiClient";

import { CookiesHelper } from "../helpers/cookies.helper";
import { AuthHelper } from "../helpers/auth.helper";
import { getInteractionLimits } from "../helpers/misc.helper";

import { EtyChat } from "../types/entities.interface";
import { EChatSource } from "../types/entities.enum";
import { HMICreateChat, HMIGenerateChatResponse } from "../types/services.interface";

export const useGetChats = (conversationId: string | null) => {
    const isSessionTokenValid = AuthHelper.isSessionTokenValid().result;

    const queryFn = async (): Promise<EtyChat[]> => {
        const token = CookiesHelper.get(`${process.env.REACT_APP__SESSION_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.get(`/chats/all/${conversationId}`);
        return res.data?.length ? res.data : [];
    };

    return useQuery({
        queryKey: ['chats', conversationId],
        queryFn,
        retry: false,
        enabled: isSessionTokenValid && !!conversationId,
    });
};

export const useCreateChat = () => {
    const { mutate: generateChatResponse } = useGenerateChatResponse();

    const mutationFn = async (data: HMICreateChat): Promise<EtyChat> => {
        const token = CookiesHelper.get(`${process.env.REACT_APP__SESSION_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.post('/chats', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsSending(true);
        },
        onSuccess: (data, variables) => {
            variables.setChats(prev => [...prev, {
                by: data.source,
                message: data.message,
                timestamp: data.created_at,
            }]);
            variables.setMessage('');
            variables.setIsTyping(false);

            variables.setBottomScroll();

            generateChatResponse({
                conversation_id: variables.payload.conversation_id,
                simulationKey: variables.simulationKey,
                setIsGeneratingResponse: variables.setIsGeneratingResponse,
                chats: variables.chats,
                setChats: variables.setChats,
                setBottomScroll: variables.setBottomScroll,
                setIsCompletionWindowVisible: variables.setIsCompletionWindowVisible,
                messageThreshold: variables.messageThreshold,
                setMessageThreshold: variables.setMessageThreshold,
                continueMode: variables.continueMode,
            });
        },
        onError: (error: any) => {
            window.alert(error?.response?.data?.message || 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setIsSending(false);
        },
    });
};

const useGenerateChatResponse = () => {
    const mutationFn = async (data: HMIGenerateChatResponse): Promise<EtyChat> => {
        const token = CookiesHelper.get(`${process.env.REACT_APP__SESSION_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.post(`/chats/generate-response/${data.conversation_id}`);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsGeneratingResponse(true);
        },
        onSuccess: (data, variables) => {
            const newChat = {
                by: data.source,
                message: data.message,
                timestamp: data.created_at,
                typingEffect: true,
            };
            const newChats = [...variables.chats, newChat];
            variables.setChats(prev => [...prev, newChat]);

            variables.setBottomScroll();

            const botMessages = newChats.filter((chat) => chat.by === EChatSource.BOT);
            const limits = getInteractionLimits(variables.simulationKey);

            if (botMessages.length >= limits.level2) {
                variables.setMessageThreshold('level2');

                setTimeout(() => {
                    variables.setIsCompletionWindowVisible(true);
                }, 5000);
            } else if (!variables.continueMode && botMessages.length >= limits.level1) {
                variables.setMessageThreshold('level1');

                setTimeout(() => {
                    variables.setIsCompletionWindowVisible(true);
                }, 5000);
            };
        },
        onError: (error: any) => {
            window.alert(error?.response?.data?.message || 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setIsGeneratingResponse(false);
        },
    });
};